import type { TFunction } from "i18next";
import type { ApplyOutcome } from "~produce/production/schedule/components/reapplyRecipes/useReapplyRecipesMutations";

import { ReapplyRecipesRunStatus } from "~/gql/graphqlBase.generated";
import {
	type GetReapplyRecipesRunQuery,
	type ReapplyRecipesRunFieldsFragment
} from "~/gql/queries/produce/reapplyRecipes.generated";

export const REAPPLY_RUN_POLL_INTERVAL_MS = 3_000;
export const REAPPLY_RUN_MAX_MISSED_READS = 20;

export interface ReapplyRunNotice {
	message: string;
	variant: "error" | "success" | "warning";
}

type ReapplyRunProgress = Pick<ReapplyRecipesRunFieldsFragment, "processedJobs" | "totalJobs">;

interface ReapplyRunRead {
	data?: GetReapplyRecipesRunQuery;
	error?: unknown;
}

export const isReapplyRunActive = (status: ReapplyRecipesRunStatus): boolean =>
	status === ReapplyRecipesRunStatus.Queued || status === ReapplyRecipesRunStatus.Running;

export const countMissedReapplyRunReads = (missedReads: number, { data, error }: ReapplyRunRead): number => {
	if (data?.reapplyRecipesRun && !error) return 0;
	return error || data ? missedReads + 1 : missedReads;
};

export const computeReapplyRunProgressPercent = ({ processedJobs, totalJobs }: ReapplyRunProgress): number =>
	totalJobs > 0 ? Math.floor((processedJobs / totalJobs) * 100) : 0;

export const formatReapplyRunProgressLabel = (
	{ processedJobs, totalJobs }: ReapplyRunProgress,
	t: TFunction<"produce">
) =>
	t("schedule.reapplyRecipes.run.progress", {
		processed: processedJobs.toLocaleString(),
		total: totalJobs.toLocaleString()
	});

const countFailedJobs = ({ errors, failedResults }: ReapplyRecipesRunFieldsFragment): number => {
	const failedJobIds = new Set(failedResults.map((result) => result.productionJobId));
	for (const error of errors) {
		if (error.productionJobId) failedJobIds.add(error.productionJobId);
	}
	return failedJobIds.size;
};

const buildReapplyRunCompletionNotice = (
	run: ReapplyRecipesRunFieldsFragment,
	t: TFunction<"produce">
): ReapplyRunNotice => {
	const failedJobCount = countFailedJobs(run);
	const counts = {
		applied: run.appliedJobs.toLocaleString(),
		count: run.appliedJobs,
		failed: failedJobCount.toLocaleString()
	};
	if (run.status === ReapplyRecipesRunStatus.Failed) {
		return { message: t("schedule.reapplyRecipes.run.stopped", counts), variant: "error" };
	}
	const hasFailures = failedJobCount > 0 || run.errors.length > 0;
	return { message: t("schedule.reapplyRecipes.run.finished", counts), variant: hasFailures ? "warning" : "success" };
};

export const reapplyRunToApplyOutcome = (run: ReapplyRecipesRunFieldsFragment): ApplyOutcome => ({
	applyError: null,
	errors: run.errors,
	failedResults: run.failedResults,
	ok: run.status === ReapplyRecipesRunStatus.Completed && run.errors.length === 0 && run.failedResults.length === 0
});

interface ReapplyRunStart {
	entity: string | null | undefined;
	errors: ApplyOutcome["errors"];
}

export interface ReapplyRunStartOutcome {
	outcome: ApplyOutcome;
	runIdToTrack: string | null;
}

export const classifyReapplyRunStart = (
	{ entity: runId, errors }: ReapplyRunStart,
	applyErrorMessage: string
): ReapplyRunStartOutcome => {
	if (errors.length > 0) {
		return { outcome: { applyError: null, errors, failedResults: [], ok: false }, runIdToTrack: null };
	}
	if (!runId) {
		return { outcome: { applyError: applyErrorMessage, errors: [], failedResults: [], ok: false }, runIdToTrack: null };
	}
	return { outcome: { applyError: null, errors: [], failedResults: [], ok: true }, runIdToTrack: runId };
};

export const mergeResumedReapplyRunIds = (
	watchedRunIds: string[],
	resumedRunIds: string[],
	finishedRunIds: string[]
): string[] => [
	...watchedRunIds,
	...resumedRunIds.filter((runId) => !watchedRunIds.includes(runId) && !finishedRunIds.includes(runId))
];

export default buildReapplyRunCompletionNotice;
